"use client";

import { useMemo, useRef, useState } from "react";
import { Download, RefreshCcw, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import JSZip from "jszip";
import type { Task } from "@/client/hooks/useTaskManager";
import { TaskStatus } from "@/client/hooks/useTaskManager";
import { extractWithConfig } from "@/client/utils/outputNormalize";
import { getSoraVideoId } from "@/client/utils/taskUtils";
import { triggerBlobDownload } from "@/client/utils/fileSaver";
import { fetchSoraVideoContent } from "@/client/api/sora";
import { useLanguageStore } from "@/client/store/languageStore";
import { findModelConfig } from "@/client/config/models.config";
import { parseSubmittedTaskCsvContent } from "../utils/resultCsvParser";
import { useBatchResultProgress } from "../hooks/useBatchResultProgress";
import { getBatchArchiveFilename } from "../utils/filenameLocales";

interface BatchResultDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FileFeedback {
  severity: "success" | "info" | "warning" | "error";
  message: string;
  details?: string[];
}

interface MediaAssetDescriptor {
  url: string;
  type: "video" | "image";
  index: number;
  soraVideoId?: string;
  preferredName?: string;
}

const statusMeta: Record<
  TaskStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    className?: string;
  }
> = {
  [TaskStatus.SUBMITTING]: { label: "提交中", variant: "secondary" },
  [TaskStatus.PENDING]: { label: "排队中", variant: "secondary" },
  [TaskStatus.RUNNING]: { label: "生成中", variant: "secondary" },
  [TaskStatus.SUCCESS]: { label: "已完成", variant: "default" },
  [TaskStatus.FAILED]: { label: "已失败", variant: "destructive" },
};

const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]+/g;

const sanitizeFilename = (value?: string | null, fallback = "file"): string => {
  const trimmed = (value ?? "").trim();
  const base = trimmed || fallback;
  const withoutInvalidSymbols = base.replace(INVALID_FILENAME_CHARS, "_");
  const withoutControlChars = Array.from(withoutInvalidSymbols, (char) =>
    char.charCodeAt(0) < 32 ? "_" : char
  ).join("");
  const normalized = withoutControlChars.replace(/_+/g, "_");
  const result = normalized || fallback;
  return result.slice(0, 64);
};

const inferExtensionFromMime = (mime?: string): string | undefined => {
  if (!mime) return undefined;
  if (mime.startsWith("image/")) return mime.split("/")[1];
  if (mime.startsWith("video/")) return mime.split("/")[1];
  switch (mime) {
    case "image/jpeg":
      return "jpg";
    case "application/octet-stream":
      return undefined;
    default:
      return undefined;
  }
};

const extractFilenameFromUrl = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    const match = parsed.pathname.match(/\/([^/]+)$/);
    if (!match) return undefined;
    return match[1];
  } catch {
    const pathMatch = url.match(/([^/?#]+)(?:\?|#|$)/);
    return pathMatch ? pathMatch[1] : undefined;
  }
};

const decodeDataUrl = (url: string): { blob: Blob; mime: string | undefined } | null => {
  const match = url.match(/^data:(.*?);base64,(.*)$/);
  if (!match) {
    return null;
  }
  const [, mime, base64Data] = match;
  try {
    const binary = atob(base64Data);
    const len = binary.length;
    const buffer = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
      buffer[i] = binary.charCodeAt(i);
    }
    return { blob: new Blob([buffer], { type: mime || "application/octet-stream" }), mime };
  } catch {
    return null;
  }
};

const fetchMediaBlob = async (
  descriptor: MediaAssetDescriptor
): Promise<{ blob: Blob; filename: string }> => {
  const { type, index, preferredName, soraVideoId } = descriptor;
  let { url } = descriptor;

  const baseName = preferredName
    ? sanitizeFilename(preferredName)
    : sanitizeFilename(`${type}-${index + 1}`);

  if (soraVideoId) {
    try {
      const result = await fetchSoraVideoContent(soraVideoId);
      if (result.blob) {
        const blob = result.blob;
        const extension =
          inferExtensionFromMime(blob.type) ||
          inferExtensionFromMime(result.blob.type) ||
          (type === "video" ? "mp4" : "bin");
        const resolved =
          result.filename && result.filename.trim()
            ? sanitizeFilename(result.filename)
            : `${baseName}.${extension}`;
        return { blob, filename: resolved };
      }
      if (result.videoUrl) {
        url = result.videoUrl;
      }
    } catch (error) {
      console.warn("[BatchResultDialog] 通过 Sora 下载接口获取资源失败", error);
    }
  }

  if (!url) {
    throw new Error("资源地址为空");
  }

  if (url.startsWith("data:")) {
    const decoded = decodeDataUrl(url);
    if (!decoded) {
      throw new Error("解析 data URL 失败");
    }
    const extension = inferExtensionFromMime(decoded.mime) || (type === "video" ? "mp4" : "png");
    return { blob: decoded.blob, filename: `${baseName}.${extension}` };
  }

  const response = await fetch(url, {
    credentials: "omit",
  });
  if (!response.ok) {
    throw new Error(`下载失败 (${response.status})`);
  }
  const blob = await response.blob();
  const mimeExt = inferExtensionFromMime(blob.type);
  const urlFilename = extractFilenameFromUrl(url);
  const extMatch = urlFilename?.match(/\.([A-Za-z0-9]+)$/);
  const extFromUrl = extMatch ? extMatch[1] : undefined;
  const extension = (extFromUrl || mimeExt || (type === "video" ? "mp4" : "png")).toLowerCase();
  const normalizedBase = urlFilename
    ? sanitizeFilename(urlFilename.replace(/\.[^.]+$/, ""), baseName)
    : baseName;
  const filename = normalizedBase.endsWith(`.${extension}`)
    ? normalizedBase
    : `${normalizedBase}.${extension}`;
  return { blob, filename };
};

const gatherMediaAssets = (task: Task | null | undefined): MediaAssetDescriptor[] => {
  if (!task || !task.result) {
    return [];
  }
  const extractorConfig = findModelConfig(task.model)?.workspace?.resultExtractor;
  const { videos, images } = extractWithConfig(task.result, extractorConfig);
  const soraVideoId = getSoraVideoId(task) ?? undefined;
  const modelLabel = task.model ? sanitizeFilename(task.model) : "task";
  const taskLabel = task.id ? sanitizeFilename(task.id) : "task";
  const videoAssets: MediaAssetDescriptor[] = videos.map((url, index) => ({
    url,
    type: "video",
    index,
    soraVideoId,
    preferredName: `${modelLabel}-${taskLabel}-video-${index + 1}`,
  }));
  if (videoAssets.length === 0 && soraVideoId) {
    videoAssets.push({
      url: "",
      type: "video",
      index: 0,
      soraVideoId,
      preferredName: `${modelLabel}-${taskLabel}-video-1`,
    });
  }
  const imageAssets: MediaAssetDescriptor[] = images.map((url, index) => ({
    url,
    type: "image",
    index,
    preferredName: `${modelLabel}-${taskLabel}-image-${index + 1}`,
  }));
  return [...videoAssets, ...imageAssets];
};

const formatTimestamp = (timestamp?: number | null): string => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
};

const PromptCell: React.FC<{ value: string }> = ({ value }) => (
  <div className="text-sm leading-5 line-clamp-3 break-words max-h-18">{value || "-"}</div>
);

export const BatchResultDialog: React.FC<BatchResultDialogProps> = ({ open, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileFeedback, setFileFeedback] = useState<FileFeedback | null>(null);
  const [packaging, setPackaging] = useState(false);
  const [packageMessage, setPackageMessage] = useState<FileFeedback | null>(null);
  const language = useLanguageStore((state) => state.language);
  const {
    records,
    loading,
    summary,
    lastUpdatedAt,
    loadRows,
    refresh,
    clear,
  } = useBatchResultProgress();

  const completedRecords = useMemo(
    () => records.filter((record) => record.task?.status === TaskStatus.SUCCESS),
    [records]
  );

  const handleClose = () => {
    clear();
    setFileFeedback(null);
    setPackageMessage(null);
    setPackaging(false);
    onClose();
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPackageMessage(null);

    try {
      const text = await file.text();
      const { rows, warnings, errors } = parseSubmittedTaskCsvContent(text);
      if (errors.length) {
        setFileFeedback({
          severity: "error",
          message: errors[0],
          details: errors.slice(1),
        });
        clear();
        return;
      }
      setFileFeedback({
        severity: warnings.length ? "warning" : "success",
        message: warnings.length ? "CSV 解析完成，但存在提示信息。" : "CSV 导入成功。",
        details: warnings.length ? warnings : undefined,
      });
      await loadRows(rows);
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : "读取文件失败，请重试。";
      setFileFeedback({
        severity: "error",
        message,
      });
      clear();
    } finally {
      event.target.value = "";
    }
  };

  const handleRefresh = async () => {
    setPackageMessage(null);
    await refresh();
  };

  const handleDownloadZip = async () => {
    setPackageMessage(null);
    if (!completedRecords.length) {
      setPackageMessage({
        severity: "info",
        message: "当前没有已完成的任务可供下载。",
      });
      return;
    }

    setPackaging(true);
    const zip = new JSZip();
    const usedZipPaths = new Set<string>();
    const ensureUniqueFilename = (rawName: string): string => {
      const candidate = rawName.trim() || "file";
      if (!usedZipPaths.has(candidate)) {
        usedZipPaths.add(candidate);
        return candidate;
      }
      const dotIndex = candidate.lastIndexOf(".");
      const base = dotIndex === -1 ? candidate : candidate.slice(0, dotIndex);
      const ext = dotIndex === -1 ? "" : candidate.slice(dotIndex);
      let index = 2;
      let next = `${base}-${index}${ext}`;
      while (usedZipPaths.has(next)) {
        index += 1;
        next = `${base}-${index}${ext}`;
      }
      usedZipPaths.add(next);
      return next;
    };
    const meta: Array<{
      taskId: string | null;
      model: string | null;
      prompt: string;
      csvRow?: string;
      soraVideoId: string | null;
      assetCount: number;
    }> = [];
    const warnings: string[] = [];

    for (let i = 0; i < completedRecords.length; i += 1) {
      const record = completedRecords[i];
      const task = record.task;
      if (!task) {
        continue;
      }
      const folderLabel = sanitizeFilename(
        `${String(i + 1).padStart(2, "0")}-${task.model || "task"}-${task.id || "unknown"}`,
        `task-${i + 1}`
      );
      const taskJsonName = ensureUniqueFilename(`${folderLabel}-task.json`);
      zip.file(
        taskJsonName,
        JSON.stringify(
          {
            taskId: task.id,
            model: task.model,
            status: task.status,
            prompt: task.prompt,
            submittedAt: task.submittedAt,
            finishedAt: task.finishedAt,
            rawRow: record.row,
          },
          null,
          2
        )
      );

      const assets = gatherMediaAssets(task);
      if (!assets.length) {
        warnings.push(`任务 ${task.id ?? record.row.taskId} 未检测到可下载的媒体资源。`);
      }

      let assetCount = 0;
      for (const asset of assets) {
        try {
          const { blob, filename } = await fetchMediaBlob(asset);
          const uniqueName = ensureUniqueFilename(filename);
          zip.file(uniqueName, blob);
          assetCount += 1;
        } catch (error) {
          const message =
            error instanceof Error && error.message ? error.message : "媒体下载失败。";
          warnings.push(
            `任务 ${task.id ?? record.row.taskId} 的 ${asset.type} #${asset.index + 1} 下载失败：${message}`
          );
        }
      }

      meta.push({
        taskId: task.id ?? null,
        model: task.model ?? null,
        prompt: task.prompt ?? "",
        csvRow: record.row.csvRow,
        soraVideoId: getSoraVideoId(task) ?? null,
        assetCount,
      });
    }

    const metadataName = ensureUniqueFilename("metadata.json");
    zip.file(metadataName, JSON.stringify(meta, null, 2));

    try {
      const archive = await zip.generateAsync({ type: "blob" });
      const filename = getBatchArchiveFilename(language);
      await triggerBlobDownload(archive, filename);
      setPackageMessage({
        severity: warnings.length ? "warning" : "success",
        message: `已打包 ${completedRecords.length} 个任务结果。`,
        details: warnings.length ? warnings : undefined,
      });
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : "压缩文件生成失败。";
      setPackageMessage({
        severity: "error",
        message,
      });
    } finally {
      setPackaging(false);
    }
  };

  const totalCount = summary.total;
  const finishedCount = summary.completed;
  const failedCount = summary.failed;
  const runningCount = summary.running;
  const pendingCount = summary.pending;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-3xl md:max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>批量任务进度</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleFileChange}
              />
              <Button onClick={handleSelectFile} disabled={loading}>
                <Upload className="mr-1" /> 选择 CSV
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="outline" onClick={handleRefresh} disabled={loading || !records.length}>
                      <RefreshCcw className="mr-1" /> 刷新
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>重新获取最新状态</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {fileFeedback ? (
            <Alert className="mt-1" variant={fileFeedback.severity === "error" ? "destructive" : "default"}>
              <AlertDescription>
                <div className="text-sm">{fileFeedback.message}</div>
                {fileFeedback.details?.map((line, index) => (
                  <div key={`${line}-${index}`} className="text-xs text-muted-foreground">{line}</div>
                ))}
              </AlertDescription>
            </Alert>
          ) : null}

          {packageMessage ? (
            <Alert className="mt-1" variant={packageMessage.severity === "error" ? "destructive" : "default"}>
              <AlertDescription>
                <div className="text-sm">{packageMessage.message}</div>
                {packageMessage.details?.map((line, index) => (
                  <div key={`${line}-${index}`} className="text-xs text-muted-foreground">{line}</div>
                ))}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">总数：{totalCount}</Badge>
              <Badge>完成：{finishedCount}</Badge>
              <Badge variant="destructive">失败：{failedCount}</Badge>
              <Badge variant="secondary">生成中：{runningCount}</Badge>
              <Badge variant="secondary">排队中：{pendingCount}</Badge>
            </div>
            <div className="text-xs text-muted-foreground md:ml-auto">
              {lastUpdatedAt ? `上次更新：${formatTimestamp(lastUpdatedAt)}` : "尚未加载数据"}
            </div>
          </div>

          <div className="min-h-[200px] max-h-[420px] overflow-auto border rounded-md flex-shrink">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[64px]">序号</TableHead>
                  <TableHead>Prompt</TableHead>
                  <TableHead className="w-[140px]">生成方案</TableHead>
                  <TableHead className="w-[160px]">任务 ID</TableHead>
                  <TableHead className="w-[100px]">状态</TableHead>
                  <TableHead className="w-[120px]">进度</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="text-center text-sm text-muted-foreground py-8">请上传包含任务 ID 的 CSV 文件以查询进度。</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record, index) => {
                    const task = record.task;
                    const status = task?.status ?? TaskStatus.PENDING;
                    const meta = statusMeta[status];
                    const progressValue = task?.progress ?? 0;
                    const modelLabel = task?.model || record.row.modelKey || "-";
                    return (
                      <TableRow key={`${record.row.taskId}-${index}`}>
                        <TableCell>
                          <div className="flex flex-col">
                            <div className="text-sm font-semibold">{record.row.csvRow ?? record.row.dataIndex ?? index + 1}</div>
                            <div className="text-xs text-muted-foreground">{index + 1}</div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[360px]"><PromptCell value={task?.prompt ?? record.row.prompt ?? ""} /></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{modelLabel}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{task?.id || record.row.taskId || "-"}</TableCell>
                        <TableCell>
                          <Badge variant={meta.variant} className={meta.className}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={progressValue} className="w-24" />
                            <span className="text-xs text-muted-foreground w-8 text-right">{progressValue ? `${progressValue}%` : "-"}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1 flex-shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="default" onClick={handleDownloadZip} disabled={packaging || completedRecords.length === 0}>
                      {packaging ? (
                        <span className="flex items-center gap-2">
                          <span className="size-3 rounded-full border-2 border-background border-t-transparent animate-spin" />
                          正在打包...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2"><Download className="size-4" /> 打包下载完成结果</span>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{completedRecords.length ? "" : "暂无已完成任务"}</TooltipContent>
              </Tooltip>
              <Button variant="outline" onClick={handleClose}>关闭</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchResultDialog;
